import { $Locale } from '@lemon8/web-app-shared/atom';
import { useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { useAtomValue } from 'jotai';
import useReflowClick from '~/utils/use-reflow-click';
import { useViewTracking } from '~/utils/use-view-tracking';
import { useCallback, useMemo } from 'react';
import { SearchWord } from 'shared/models';
import { $CurrentPageModule } from '~/models/current-page-module';
import { $CurrentPosition } from '~/models/current-position';
import { SearchKeywordSource } from '~/utils/search-source';
import { usePageActive } from '~/utils/active/use-page-active';
import { $CurrentModuleRank, MODULE_NOT_RANK } from '~/models/current-module-rank';
import { kepQueryFormat } from 'shared/utils';
import { SEOKeywordsItem } from '../seo-keywords-item';

interface IHotKeywordItemProps {
  item: SearchWord;
  index: number;
  moduleRank: number;
}

export const WordItem = (props: IHotKeywordItemProps): JSX.Element => {
  const { item, index, moduleRank } = props;
  const { word, wordType } = item;
  const { region } = useAtomValue($Locale);

  const currentPageModule = useScopedAtomValue($CurrentPageModule);
  const currentPosition = useScopedAtomValue($CurrentPosition);
  const currentModuleRank = useScopedAtomValue($CurrentModuleRank);

  const href = useMemo(() => `/discover/${kepQueryFormat(word)}?region=${region}`, [word]);

  const active = usePageActive();
  const activeToSearch = useCallback(() => {
    const ampExtra: Record<string, any> = {
      enter_position: currentPosition,
      seo_search_keyword: kepQueryFormat(word),
      seo_search_keyword_source: SearchKeywordSource[wordType],
    };

    if (currentModuleRank !== MODULE_NOT_RANK) {
      ampExtra['seo_position_rank'] = currentModuleRank;
    }

    active({
      schemaOption: {
        pageType: 'search',
        query: kepQueryFormat(word),
      },
      ampExtra,
    });
  }, []);

  const handleKeywordClick = useReflowClick(
    {
      reflowBehavior: () => {
        activeToSearch();
      },
      clickEvent: () => ({
        current_page_module: currentPageModule,
        current_position: currentPosition,
        search_keyword: kepQueryFormat(word),
        search_keyword_source: SearchKeywordSource[wordType],
        rank: index,
        ...(currentModuleRank !== MODULE_NOT_RANK ? { module_rank: moduleRank } : {}),
      }),
    },
    [activeToSearch],
  );

  const kepHotKeywordObservedRef = useViewTracking<HTMLAnchorElement>({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_page_module: currentPageModule,
        current_position: currentPosition,
        search_keyword: kepQueryFormat(word),
        search_keyword_source: SearchKeywordSource[wordType],
        rank: index,
        ...(currentModuleRank !== MODULE_NOT_RANK ? { module_rank: moduleRank } : {}),
      });
    }, []),
  });

  return (
    <a
      href={href}
      key={`search-words-list-item-${index}`}
      className="search-words-list-item"
      onClick={handleKeywordClick}
      ref={kepHotKeywordObservedRef}
    >
      <div key={`search-words-list-item-text-${index}`} className="search-words-list-item-text">
        <SEOKeywordsItem keyword={word} />
      </div>
    </a>
  );
};
